import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FlagIcon } from '@heroicons/react/24/outline';

// Signalements de projets envoyés depuis l'application. À 3 signalements en attente,
// le serveur a déjà remis le projet en modération (retiré de la liste publique).
const MOTIFS = {
  arnaque: 'Arnaque ou fraude',
  interdit: 'Contenu ou activité interdits',
  choquant: 'Contenu choquant',
  faux: 'Projet trompeur',
  autre: 'Autre',
};

const ProjectReports = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery('admin-project-reports', async () => {
    const response = await axios.get('/api/admin/reports');
    return response.data?.reports || [];
  });
  const reports = data || [];

  const refresh = () => {
    queryClient.invalidateQueries('admin-project-reports');
    queryClient.invalidateQueries('admin-projects');
  };
  const remove = useMutation(
    (projectId) => axios.post(`/api/admin/reports/project/${projectId}/remove`),
    { onSuccess: () => { refresh(); toast.success('Projet retiré'); }, onError: () => toast.error('Retrait impossible') }
  );
  const dismiss = useMutation(
    (id) => axios.post(`/api/admin/reports/${id}/dismiss`),
    { onSuccess: () => { refresh(); toast.success('Signalement classé sans suite'); }, onError: () => toast.error('Action impossible') }
  );

  // Plusieurs personnes peuvent signaler le même projet : regroupement par projet
  const byProject = reports.reduce((acc, r) => { (acc[r.project_id] = acc[r.project_id] || []).push(r); return acc; }, {});
  const busy = remove.isLoading || dismiss.isLoading;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
        <FlagIcon className="h-5 w-5 text-red-600" />
        <h2 className="font-semibold text-text">Signalements à traiter ({reports.length})</h2>
      </div>
      {isLoading ? (
        <p className="px-6 py-4 text-sm text-gray-500">Chargement…</p>
      ) : reports.length === 0 ? (
        <p className="px-6 py-4 text-sm text-gray-500">Aucun signalement en attente.</p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {Object.values(byProject).map((group) => {
            const first = group[0];
            const project = first.project;
            return (
              <li key={first.project_id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-text">{project?.title || 'Projet supprimé'}</p>
                  <p className="text-xs text-gray-500">Statut : {project?.status || '—'} · {group.length} signalement(s)</p>
                  <ul className="mt-2 space-y-1">
                    {group.map((r) => (
                      <li key={r.id} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="font-medium">{MOTIFS[r.motif] || r.motif}</span>
                        {r.details && <span className="text-gray-500">— {r.details}</span>}
                        <button type="button" onClick={() => dismiss.mutate(r.id)} disabled={busy}
                          className="ml-auto text-xs text-gray-500 hover:underline whitespace-nowrap">Sans suite</button>
                      </li>
                    ))}
                  </ul>
                </div>
                <button type="button" onClick={() => { if (window.confirm(`Retirer le projet « ${project?.title || ''} » ?`)) remove.mutate(first.project_id); }}
                  disabled={busy || project?.status === 'rejected'}
                  className="self-start px-3 py-1.5 rounded-lg text-sm font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 whitespace-nowrap">
                  Retirer le projet
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default ProjectReports;
